import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';

import {
  AssociationDescriptionDto,
  RegisterDescriptionDto,
  Client,
  DlmsAttributes,
  DlmsDataType,
  DlmsObjectType,
  MeterModelDataOut,
  MeterModelVersionsNew,
  MeterSetupVersionDto,
  RecommendedRegisterDto,
  RecommendedRegisterGroupDto,
  SetupConfigurationDataIn,
  SetupConfigurationDto,
  SetupConfigurationOptionDto,
  SetupConfigurationOptionsDto,
} from '../../../../../api/api-reference';
import { FileService } from '../../../../../services/file.service';
import { AccordionComponent } from "../../../../core/components/accordion/accordion.component";
import { SetupConfiguration } from './model/setup-configuration';
import {
  RecommendedRegisterRow,
  RecommendedRegisterRowGroup,
  toRecommendedRegisterRow,
} from './model/recommended-registers';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { SearchComponent } from '../../../../core/components/search/search.component';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../../../../core/components/confirm-dialog/confirm-dialog.component';
@Component({
  selector: 'app-meter-model-associations-setup',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    AccordionComponent,
    SearchComponent,
    ScrollingModule,
    ConfirmDialogComponent
  ],
  templateUrl: './meter-model-associations-setup.component.html',
  styleUrl: './meter-model-associations-setup.component.scss',
})
export class MeterModelAssociationsSetupComponent implements OnInit {
  activeTab = 'associations';
  activeIntervalTab = 'interval-1';
  activeLpTab = 'lp-1';
  highlightedAttribute: DlmsAttributes | undefined = undefined;
  private highlightTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly highlightDurationMs = 2000;

  // recommended registers are preselected only while the first version of a meter model is being created
  private isFirstVersion = false;
  private lpDefaultsApplied: Record<string, Set<DlmsAttributes>> = {};
  private recommendedRegistersPerLpTab: Record<string, RecommendedRegisterGroupDto[]> = {};
  readonly lpSelectionLimit = 27;
  recommendedCollapsed = true;

  associations: AssociationDescriptionDto[] = [];
  filteredGroups: FormGroup[] = [];
  private searchText = '';
  registers: RegisterDescriptionDto[] = [];
  LP1SetupConfiguration: SetupConfiguration[] = [];
  LP2SetupConfiguration: SetupConfiguration[] = [];
  LP3SetupConfiguration: SetupConfiguration[] = [];
  PushOnInterval1RequiredCaptureObjectsConfiguration: SetupConfiguration[] = [];
  PushOnInterval2RequiredCaptureObjectsConfiguration: SetupConfiguration[] = [];
  PushOnInterval3RequiredCaptureObjectsConfiguration: SetupConfiguration[] = [];
  PushOnAlarmCaptureObjectsConfiguration: SetupConfiguration[] = [];
  PushOnConnectRequiredCaptureObjectsConfiguration: SetupConfiguration[] = [];
  PushOnPowerDownRequiredCaptureObjectsConfiguration: SetupConfiguration[] = [];
  PushOnInstallationRequiredCaptureObjectsConfiguration: SetupConfiguration[] = [];

  selectedMeterModel: string | undefined = undefined;
  selectedVersion: string | undefined = undefined;
  isSaveModalOpen: boolean = false;
  isInputMeterNumberModalOpen: boolean = false;
  isReadOnly: boolean = false;
  meterNumber: string = '';

  // hidden debug: in browser console run localStorage.setItem('hesDebug','1') and refresh; remove with localStorage.removeItem('hesDebug')
  showDebug: boolean = localStorage.getItem('hesDebug') === '1';

  debugTitle(row: SetupConfiguration, config: SetupConfiguration[]): string | null {
    if (!this.showDebug) {
      return null;
    }
    return `alwaysIncluded=${row.alwaysIncluded} | isReadOnly=${this.isReadOnly} | disabledByOtherList=${row.disabledByOtherList} | limitReached=${this.isLpSelectionLimitReached(config)} | configure=${row.configure}`;
  }

  DlmsAttributes = DlmsAttributes;

  // attributes allowed in multiple load profile lists at the same time
  private readonly lpSharedAttributes = new Set<DlmsAttributes>([DlmsAttributes.Clock_TimeZone]);

  meterModel: MeterModelDataOut = new MeterModelDataOut();

  form = this.formBuilder.group({
    associations: this.formBuilder.array<FormGroup>([]),
  });

  private readonly obisRegex = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

  get associationsFormArray(): FormArray<FormGroup> {
    return this.form.get('associations') as FormArray<FormGroup>;
  }

  get associationGroups(): FormGroup[] {
    return this.associationsFormArray.controls as FormGroup[];
  }

  get filteredUnsupportedRegisters(): RegisterDescriptionDto[] {
    return this.registers.filter(ua => !ua.inUse);
  }

  private registeredSubscriptions: Subscription[] = [];

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private fileService: FileService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit(): void {
    this.meterModel.model = history.state['model'];
    this.meterModel.firmwareVersion = history.state['firmwareVersion'];

    this.route.queryParams.subscribe(params => {
      const versionId = params['versionId'] ?? null;
      const meterModelId = params['meterModelId'] ?? null;

      if (versionId) {
        this.isReadOnly = true;
        this.selectedMeterModel = undefined;
        this.selectedVersion = versionId;
        this.getVersionsAssociations();
      } else if (meterModelId) {
        this.selectedMeterModel = meterModelId.toString();
        this.selectedVersion = undefined;
        this.isReadOnly = false;
        this.getMeterModelsAssociations();
      } else {
        this.isReadOnly = true;
        this.selectedMeterModel = undefined;
      }
    });
  }

  ngOnDestroy(): void {
    this.registeredSubscriptions.forEach(sub => sub.unsubscribe());
    this.registeredSubscriptions = [];
    clearTimeout(this.highlightTimeoutId);
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  goBack(): void {
    this.location.back();
  }

  searchChange(searchText: string) {
    this.searchText = searchText.trim().toLowerCase();
    this.updateFilteredGroups();
  }

  private updateFilteredGroups() {
    const groups = this.associationsFormArray.controls;
    this.filteredGroups = this.searchText
      ? groups.filter(g =>
          g.get('name')?.value.toLowerCase().includes(this.searchText) ||
          g.get('obis')?.value.toLowerCase().includes(this.searchText))
      : groups.slice();
  }

  download() {
    this.client
      .downloadFileMeterModelAssociations(this.selectedVersion)
      .subscribe({
        next: (response) => {
          this.fileService.downloadFile(
            response.data,
            response.fileName || `Version_${this.selectedVersion}`,
          );
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
  }
  
  navigate(route: string) {
    this.router.navigate([route]);
  }

  dataTypeDictionary: DlmsDataType[] = Object.keys(DlmsDataType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => DlmsDataType[key as keyof typeof DlmsDataType]);

  objectTypeDictionary: DlmsObjectType[] = Object.keys(DlmsObjectType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => DlmsObjectType[key as keyof typeof DlmsObjectType]);


  getMeterModelsAssociations() {
    this.client.getConfigurationOptions()
      .subscribe({
        next: (setupConfigurations) => {
          this.client.getMeterModelAssociations(this.selectedMeterModel).subscribe({
            next: (response) => {
              this.prepareFormData(response, setupConfigurations);
            },
            error: (err) => {
              this.toastr.error(err);
            },
          });
        },
        error: (err) => {
          this.toastr.error(err);
        }
      });
  }

  getVersionsAssociations() {
    this.client.getConfigurationOptions()
      .subscribe({
        next: (setupConfigurations) => {

          this.client.getVersionAssociations(this.selectedVersion).subscribe({
            next: (response) => {
              this.prepareFormData(response, setupConfigurations);
            },
            error: (err) => {
              this.toastr.error(err);
            },
          });
        },
        error: (err) => {
          this.toastr.error(err);
        }
      });
  }

  private prepareFormData(response: MeterSetupVersionDto, setupConfigurations: SetupConfigurationOptionsDto) {
    this.associations = response.associations!;
    this.registers = response.registers!;

    this.isFirstVersion = !this.isReadOnly && response.version === 0;

    this.recommendedRegistersPerLpTab = {
      'lp-1': setupConfigurations.lp1RecommendedRegisters ?? [],
      'lp-2': setupConfigurations.lp2RecommendedRegisters ?? [],
      'lp-3': setupConfigurations.lp3RecommendedRegisters ?? [],
    };

    this.syncUnsupportedAssociationsStatus();

    this.associationsFormArray.clear();

    this.initializeAssociationsFormArray();

    this.LP1SetupConfiguration = this.prepareSetupOptions(setupConfigurations.lpSetupConfigurationOptions!, response.lP1CaptureObjectsConfiguration!);
    this.LP2SetupConfiguration = this.prepareSetupOptions(setupConfigurations.lpSetupConfigurationOptions!, response.lP2CaptureObjectsConfiguration!);
    this.LP3SetupConfiguration = this.prepareSetupOptions(setupConfigurations.lpSetupConfigurationOptions!, response.lP3CaptureObjectsConfiguration!);
    this.PushOnInterval1RequiredCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnInterval1RequiredCaptureObjects!, []);
    this.PushOnInterval2RequiredCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnInterval2RequiredCaptureObjects!, []);
    this.PushOnInterval3RequiredCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnInterval3RequiredCaptureObjects!, []);
    this.PushOnAlarmCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnAlarmRequiredCaptureObjects!, []);
    this.PushOnConnectRequiredCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnConnectRequiredCaptureObjects!, []);
    this.PushOnPowerDownRequiredCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnPowerDownRequiredCaptureObjects!, []);
    this.PushOnInstallationRequiredCaptureObjectsConfiguration = this.prepareSetupOptions(setupConfigurations.pushOnInstallationRequiredCaptureObjects!, []);

    this.updateLpDisabledFlags();

    this.applyRecommendedDefaults();

    this.updateFilteredGroups();
  }

  private initializeAssociationsFormArray() {
    this.associations.forEach((association) => {
      this.associationsFormArray.push(
        this.formBuilder.group(
          {
            inUse: [{ value: !!association.inUse, disabled: this.isReadOnly }],
            name: [{ value: association.name, disabled: true }],
            obis: [{ value: association.obis ?? '', disabled: !association.inUse || this.isReadOnly }, [Validators.required, Validators.pattern(this.obisRegex)]],
            attributeType: [{ value: association.attributeType, disabled: !association.inUse || this.isReadOnly }],
            attributeNo: [{ value: association.attributeNo, disabled: !association.inUse || this.isReadOnly }, [Validators.min(0), Validators.max(100)]],
            objectType: [{ value: association.objectType ?? '', disabled: !association.inUse || this.isReadOnly }],
            parameter: [{ value: association.parameter, disabled: !association.inUse || this.isReadOnly }, [Validators.min(0), Validators.max(100)]],
            configuredInSetup: [{ value: !!association.configuredInSetup, disabled: !association.inUse || this.isReadOnly || !association.isConfigurable }],
          }
        )
      );
    });

    const subscriptions = this.associationGroups.map((group, i) => {
      return group.valueChanges.subscribe(() => {
        const x = group.getRawValue() as AssociationDescriptionDto;

        Object.assign(this.associations[i], x);

        this.handleSelectOfRegister(x);

        this.updateSetup(this.LP1SetupConfiguration, this.associations[i].attributes, x.inUse ?? false);
        this.updateSetup(this.LP2SetupConfiguration, this.associations[i].attributes, x.inUse ?? false);
        this.updateSetup(this.LP3SetupConfiguration, this.associations[i].attributes, x.inUse ?? false);

        this.updateFormEnabledStatus(x.inUse ?? false, group);

        if (this.relevantAttributes.includes(this.associations[i].attributes)) {
          this.clearConfigurationsForLoadProfiles();

          this.updateLpDisabledFlags();
        }

        this.applyRecommendedDefaults();

        this.syncUnsupportedAssociationsStatus();
      });
    });

    this.registeredSubscriptions.push(...subscriptions);
  }

  private relevantAttributes = [
    DlmsAttributes.ProfileGeneric_LoadProfilePeriod1_CaptureObjects,
    DlmsAttributes.ProfileGeneric_LoadProfilePeriod2_CaptureObjects,
    DlmsAttributes.ProfileGeneric_LoadProfilePeriod3_CaptureObjects,
    DlmsAttributes.PushSetup_Interval1_ObjectList,
    DlmsAttributes.PushSetup_Interval2_ObjectList,
    DlmsAttributes.PushSetup_Interval3_ObjectList
  ];

  private clearConfigurationsForLoadProfiles() {
    const associationMap = new Map<DlmsAttributes, AssociationDescriptionDto>();

    this.associations
      .filter(a => this.relevantAttributes.includes(a.attributes))
      .forEach(association => associationMap.set(association.attributes, association));

    const lp1InUse = this.isConfiguredForSetup(associationMap.get(DlmsAttributes.ProfileGeneric_LoadProfilePeriod1_CaptureObjects)!);
    const lp2InUse = this.isConfiguredForSetup(associationMap.get(DlmsAttributes.ProfileGeneric_LoadProfilePeriod2_CaptureObjects)!);
    const lp3InUse = this.isConfiguredForSetup(associationMap.get(DlmsAttributes.ProfileGeneric_LoadProfilePeriod3_CaptureObjects)!);
    const pushOnInterval1InUse = this.isConfiguredForSetup(associationMap.get(DlmsAttributes.PushSetup_Interval1_ObjectList)!);
    const pushOnInterval2InUse = this.isConfiguredForSetup(associationMap.get(DlmsAttributes.PushSetup_Interval2_ObjectList)!);
    const pushOnInterval3InUse = this.isConfiguredForSetup(associationMap.get(DlmsAttributes.PushSetup_Interval3_ObjectList)!);

    if (!lp1InUse && !pushOnInterval1InUse) {
      this.LP1SetupConfiguration
        .filter(c => !c.alwaysIncluded)
        .forEach(c => c.configure = false);
    }
    if (!lp2InUse && !pushOnInterval2InUse) {
      this.LP2SetupConfiguration
        .filter(c => !c.alwaysIncluded)
        .forEach(c => c.configure = false);
    }
    if (!lp3InUse && !pushOnInterval3InUse) {
      this.LP3SetupConfiguration
        .filter(c => !c.alwaysIncluded)
        .forEach(c => c.configure = false);
    }
  }

  private syncUnsupportedAssociationsStatus() {
    const usedObisCodes = new Set(this.associations.filter(a => a.inUse).map(a => a.obis));
    this.registers.forEach(ua => {
      ua.inUse = usedObisCodes.has(ua.obis);
    });
  }

  private handleSelectOfRegister(x: AssociationDescriptionDto) {
    if ((x.objectType === DlmsObjectType.Register || x.objectType === DlmsObjectType.ExtendedRegister) && (x.attributeNo === 2 || x.attributeNo === 3)) {
      const obis = x.obis;
      const objectType = x.objectType;

      const idx2 = this.associations.findIndex(
        (a) => a.obis === obis && a.objectType === objectType && a.attributeNo === 2
      );

      const idx3 = this.associations.findIndex(
        (a) => a.obis === obis && a.objectType === objectType && a.attributeNo === 3
      );

      if (idx2 !== -1) {
        this.associations[idx2].inUse = x.inUse;
        this.associationGroups[idx2].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx2]);
      }
      if (idx3 !== -1) {
        this.associations[idx3].inUse = x.inUse;
        this.associationGroups[idx3].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx3]);
      }
    }

    // select all limiter associations that need to be configured
    if (x.objectType === DlmsObjectType.Limiter && (x.attributeNo === 2 || x.attributeNo === 4 || x.attributeNo === 6 || x.attributeNo === 7 || x.attributeNo === 11)) {
      const obis = x.obis;

      const idx2 = this.associations.findIndex(
        (a) => a.obis === obis && a.attributeNo === 2
      );

      const idx4 = this.associations.findIndex(
        (a) => a.obis === obis && a.attributeNo === 4
      );

      const idx6 = this.associations.findIndex(
        (a) => a.obis === obis && a.attributeNo === 6
      );

      const idx7 = this.associations.findIndex(
        (a) => a.obis === obis && a.attributeNo === 7
      );

      const idx11 = this.associations.findIndex(
        (a) => a.obis === obis && a.attributeNo === 11
      );

      if (idx2 !== -1) {
        this.associations[idx2].inUse = x.inUse;
        this.associationGroups[idx2].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx2]);
      }
      if (idx4 !== -1) {
        this.associations[idx4].inUse = x.inUse;
        this.associationGroups[idx4].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx4]);
      }
      if (idx6 !== -1) {
        this.associations[idx6].inUse = x.inUse;
        this.associationGroups[idx6].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx6]);
      }
      if (idx7 !== -1) {
        this.associations[idx7].inUse = x.inUse;
        this.associationGroups[idx7].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx7]);
      }
      if (idx11 !== -1) {
        this.associations[idx11].inUse = x.inUse;
        this.associationGroups[idx11].get('inUse')?.setValue(x.inUse, { emitEvent: false });

        this.updateFormEnabledStatus(x.inUse ?? false, this.associationGroups[idx11]);
      }
    }
  }

  private updateFormEnabledStatus(inUse: boolean, group: FormGroup<any>) {
    if (!inUse || this.isReadOnly) {
      group.get('obis')?.disable({ emitEvent: false });
      group.get('attributeType')?.disable({ emitEvent: false });
      group.get('attributeNo')?.disable({ emitEvent: false });
      group.get('objectType')?.disable({ emitEvent: false });
      group.get('parameter')?.disable({ emitEvent: false });
      group.get('configuredInSetup')?.disable({ emitEvent: false });
    } else {
      group.get('obis')?.enable({ emitEvent: false });
      group.get('attributeType')?.enable({ emitEvent: false });
      group.get('attributeNo')?.enable({ emitEvent: false });
      group.get('objectType')?.enable({ emitEvent: false });
      group.get('parameter')?.enable({ emitEvent: false });
      group.get('configuredInSetup')?.enable({ emitEvent: false });
    }
  }

  getValidity(group: FormGroup, property: string): boolean {
    return group.get(property)?.invalid ?? false;
  }

  isConfigurable(group: FormGroup): boolean {
    return this.associations[this.associationsFormArray.controls.indexOf(group)]?.isConfigurable ?? false;
  }

  onSubmit() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.toastr.error('Please correct the errors in the associations before submitting.');
      return;
    }

    const request = new MeterModelVersionsNew();
    request.meterModelId = this.selectedMeterModel!;
    request.details.associations = this.associations.filter(a => a.inUse);

    if (!this.validateConfigurations()) {
      this.isSaveModalOpen = false;
      return;
    }

    this.mapConfigurations(request);

    this.client.addNewVersion(request).subscribe({
      next: () => {
        this.toastr.success('Meter Setup Version successfully added.');
        this.isSaveModalOpen = false;
        this.router.navigate(['settings/crud/meter-model']);
      },
      error: (err) => {
        this.isSaveModalOpen = false;
        this.toastr.error(err);
      },
    });
  }

  validateConfigurations(): boolean {
    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval1_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnInterval1RequiredCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Interval 1 Required Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval1_ObjectList) ||
      this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod1_CaptureObjects)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.LP1SetupConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'LP1 Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval2_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnInterval2RequiredCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Interval 2 Required Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval2_ObjectList) ||
      this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod2_CaptureObjects)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.LP2SetupConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'LP2 Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval3_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnInterval3RequiredCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Interval 3 Required Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval3_ObjectList) ||
      this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod3_CaptureObjects)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.LP3SetupConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'LP3 Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_OnAlarm_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnAlarmCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Alarm Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_OnConnect_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnConnectRequiredCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Connect Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_OnPowerDown_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnPowerDownRequiredCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Power Down Capture Object')) {
        return false;
      }
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_OnInstallation_ObjectList)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.PushOnInstallationRequiredCaptureObjectsConfiguration);
      if (!this.checkValidityOfSetup(selectedOptions, 'Push On Installation Capture Object')) {
        return false;
      }
    }

    return true;
  }

  mapConfigurations(request: MeterModelVersionsNew): void {
    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval1_ObjectList) ||
      this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod1_CaptureObjects)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.LP1SetupConfiguration);
      request.details.lP1CaptureObjectsConfiguration = selectedOptions.map(
        a => new SetupConfigurationDataIn({ attribute: a.attribute })
      );
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval2_ObjectList) ||
      this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod2_CaptureObjects)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.LP2SetupConfiguration);
      request.details.lP2CaptureObjectsConfiguration = selectedOptions.map(
        a => new SetupConfigurationDataIn({ attribute: a.attribute })
      );
    }

    if (
      this.isAssociationSelected(DlmsAttributes.PushSetup_Interval3_ObjectList) ||
      this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod3_CaptureObjects)
    ) {
      const selectedOptions = this.filterSelectedSetupOptions(this.LP3SetupConfiguration);
      request.details.lP3CaptureObjectsConfiguration = selectedOptions.map(
        a => new SetupConfigurationDataIn({ attribute: a.attribute })
      );
    }
  }

  toggleConfigure(index: number, config: SetupConfiguration[]): void {
    const configuration = config.filter(c => c.id === index)[0];

    if (configuration != null) {
      const nextValue = !configuration.configure;
      if (this.isLpConfig(config) && nextValue && configuration.disabledByOtherList) {
        return;
      }
      if (this.isLpConfig(config) && nextValue && !configuration.alwaysIncluded && this.isLpSelectionLimitReached(config)) {
        this.toastr.warning(`Maximum number of ${this.lpSelectionLimit} items reached.`);
        return;
      }

      configuration.configure = nextValue;

      if (this.isLpConfig(config) && configuration.configure && !this.lpSharedAttributes.has(configuration.attribute)) {
        this.clearLpSelectionsInOtherLists(configuration.attribute, config);
      }

      if (this.isLpConfig(config)) {
        this.updateLpDisabledFlags();
      }
    }
  }

  updateSetup(config: SetupConfiguration[], attribute: DlmsAttributes, inUse: boolean): void {
    const configuration = config.find(a => a.attribute === attribute);

    if (configuration && !configuration.alwaysIncluded) {
      configuration.shouldBeDisplayed = inUse;
    }
  }

  filterSetupsToDisplay(setups: SetupConfiguration[]): SetupConfiguration[] {
    return setups.filter(setup => setup.alwaysIncluded || setup.shouldBeDisplayed).sort((a, b) => a.id - b.id);
  }

  isConfiguredForSetup(association: AssociationDescriptionDto): boolean {
    return association.inUse && association.configuredInSetup;
  }

  isAssociationSelected(attribute: DlmsAttributes): boolean {
    return this.associations.some(
      (association) =>
        association.attributes === attribute && this.isConfiguredForSetup(association),
    );
  }

  prepareSetupOptions(options: SetupConfigurationOptionDto[], setupConfigurations: SetupConfigurationDto[]): SetupConfiguration[] {
    const filteredAssosiations = this.associations.filter(
      (association) => options.map(a => a.attribute).includes(association.attributes)
    );

    return filteredAssosiations.map(association => {
      const option = options.find(a => a.attribute === association.attributes);
      const setupConfig = setupConfigurations.find(s => s.attribute === association.attributes);

      const configure = setupConfig ? true : option!.default;

      return {
        id: option!.position,
        shouldBeDisplayed: association.inUse,
        name: association.name,
        attribute: association.attributes,
        obis: association.obis,
        configure: configure,
        alwaysIncluded: option!.default,
      } as SetupConfiguration;
    }).sort((a, b) => a.id - b.id);
  }

  filterSelectedSetupOptions(setups: SetupConfiguration[]): SetupConfiguration[] {
    return setups.filter(a => ((a.shouldBeDisplayed && a.configure) || a.alwaysIncluded));
  }

  checkValidityOfSetup(setups: SetupConfiguration[], setupName: string): boolean {
    for (const setup of setups) {
      if (!this.associations.find(x => x.attributes === setup.attribute && x.inUse)) {
        this.toastr.error(`${setupName} setup is invalid. Required Association "${setup.name}" does not exist for current associations.`);
        return false;
      }
    }
    return true;
  }

  toggleCellCheckbox(event: MouseEvent): void {
    const cell = (event.currentTarget as HTMLElement);
    const checkbox = cell.querySelector<HTMLInputElement>('input[type="checkbox"]');
    if (!checkbox) return;
    checkbox.click();
  }

  onLpSelectionAttempt(event: MouseEvent, row: SetupConfiguration, config: SetupConfiguration[]): void {
    event.stopPropagation();

    this.highlightAttribute(row.attribute);

    if (!this.isLpConfig(config) || row.configure || row.alwaysIncluded || row.disabledByOtherList) {
      return;
    }

    if (this.isLpSelectionLimitReached(config)) {
      event.preventDefault();
      this.toastr.warning(`Maximum number of 30 items reached.`);
    }
  }

  private isLpConfig(config: SetupConfiguration[]): boolean {
    return this.getLpConfigs().includes(config);
  }

  private clearLpSelectionsInOtherLists(attribute: DlmsAttributes, currentConfig: SetupConfiguration[]): void {
    this.getLpConfigs()
      .filter(config => config !== currentConfig)
      .forEach(config => {
        const other = config.find(row => row.attribute === attribute);
        if (other && !other.alwaysIncluded) {
          other.configure = false;
        }
      });
  }

  private updateLpDisabledFlags(): void {
    const configs = this.getLpConfigs();
    configs.forEach(currentConfig => {
      const otherSelected = new Set<DlmsAttributes>();
      configs
        .filter(config => config !== currentConfig)
        .forEach(config => {
          config
            .filter(row => row.configure && !row.alwaysIncluded)
            .forEach(row => otherSelected.add(row.attribute));
        });

      currentConfig.forEach(row => {
        row.disabledByOtherList = !row.alwaysIncluded && !this.lpSharedAttributes.has(row.attribute) && otherSelected.has(row.attribute);
      });
    });
  }

  private getLpConfigs(): SetupConfiguration[][] {
    return [this.LP1SetupConfiguration, this.LP2SetupConfiguration, this.LP3SetupConfiguration];
  }

  isLpSelectionLimitReached(config: SetupConfiguration[]): boolean {
    return this.lpSelectedCount(config) >= this.lpSelectionLimit;
  }

  private lpSelectedCount(config: SetupConfiguration[]): number {
    return config.filter(row => row.shouldBeDisplayed && (row.configure || row.alwaysIncluded)).length;
  }

  // everything the active load profile carries, not just the recommended registers, because this
  // is the count the lpSelectionLimit applies to
  get activeLpSelectedCount(): number {
    return this.lpSelectedCount(this.activeLpConfiguration);
  }

  lpHasSelection(config: SetupConfiguration[]): boolean {
    return this.togglableLpRows(config).some(row => row.configure);
  }

  lpAllSelected(config: SetupConfiguration[]): boolean {
    const rows = this.togglableLpRows(config);

    return rows.length > 0 && rows.every(row => row.configure);
  }

  clearLpSelection(config: SetupConfiguration[], event: Event): void {
    for (const row of this.togglableLpRows(config)) {
      if (row.configure) {
        this.toggleConfigure(row.id, config);
      }
    }

    // the click already flipped the native checked state, and it does not always change what [checked] binds to
    (event.target as HTMLInputElement).checked = this.lpHasSelection(config);
  }

  private togglableLpRows(config: SetupConfiguration[]): SetupConfiguration[] {
    return this.filterSetupsToDisplay(config).filter(row => !row.alwaysIncluded && !row.disabledByOtherList);
  }

  get recommendedGroups(): RecommendedRegisterRowGroup[] {
    const config = this.activeLpConfiguration;

    return (this.recommendedRegistersPerLpTab[this.activeLpTab] ?? []).map(group => {
      const rows = (group.registers ?? []).map(register => this.buildRecommendedRow(register, config));
      return {
        titleKey: group.titleKey ?? '',
        rows: rows,
      };
    });
  }

  get allRecommendedSelected(): boolean {
    const rows = this.togglableRecommendedRows;

    return rows.length > 0 && rows.every(row => row.state === 'selected');
  }

  get someRecommendedSelected(): boolean {
    const rows = this.togglableRecommendedRows;

    return rows.some(row => row.state === 'selected') && !rows.every(row => row.state === 'selected');
  }

  isRecommendedRowDisabled(row: RecommendedRegisterRow): boolean {
    return this.isReadOnly || !!row.locked || row.state === 'inOtherList' || row.state === 'unavailable';
  }

  recommendedRowTitle(row: RecommendedRegisterRow): string | null {
    switch (row.state) {
      case 'unavailable':
        return 'Association is not selected for this meter model. Select it on the Associations tab first.';
      case 'inOtherList':
        return 'Already selected in another load profile list.';
      default:
        return null;
    }
  }

  highlightAttribute(attribute: DlmsAttributes): void {
    this.highlightedAttribute = attribute;

    clearTimeout(this.highlightTimeoutId);
    this.highlightTimeoutId = setTimeout(() => this.highlightedAttribute = undefined, this.highlightDurationMs);
  }

  focusRecommended(row: RecommendedRegisterRow): void {
    this.highlightAttribute(row.attribute);
    this.scrollToLpRow(row.attribute);
  }

  toggleRecommended(row: RecommendedRegisterRow): void {
    if (this.isRecommendedRowDisabled(row)) {
      return;
    }

    const config = this.activeLpConfiguration;
    const configuration = config.find(c => c.attribute === row.attribute);

    if (configuration) {
      this.toggleConfigure(configuration.id, config);
      this.focusRecommended(row);
    }
  }

  toggleAllRecommended(event: Event): void {
    this.applyRecommendedSelection();

    // the click already flipped the native checked state, and it does not always change what [checked] binds to
    (event.target as HTMLInputElement).checked = this.allRecommendedSelected;
  }

  private applyRecommendedSelection(): void {
    const config = this.activeLpConfiguration;
    const rows = this.togglableRecommendedRows;

    // anything already selected means the click is a deselect, otherwise a partial selection could never be cleared
    const targetState = rows.some(row => row.state === 'selected') ? 'selected' : 'missing';

    // all or nothing: selecting part of the list and stopping at the limit leaves the checkbox
    // indeterminate with no way to tell which registers were left out
    if (targetState === 'missing') {
      const overflow = this.lpSelectedCount(config) + rows.length - this.lpSelectionLimit;

      if (overflow > 0) {
        this.toastr.warning(
          `Selecting all recommended registers would exceed the maximum of ${this.lpSelectionLimit} registers. `
          + `Deselect ${overflow} ${overflow === 1 ? 'register' : 'registers'} in the list on the left first.`
        );

        return;
      }
    }

    for (const row of rows) {
      if (row.state !== targetState) {
        continue;
      }

      const configuration = config.find(c => c.attribute === row.attribute);

      if (configuration) {
        this.toggleConfigure(configuration.id, config);
      }
    }
  }

  private get togglableRecommendedRows(): RecommendedRegisterRow[] {
    return this.recommendedGroups
      .flatMap(group => group.rows)
      .filter(row => !row.locked && (row.state === 'selected' || row.state === 'missing'));
  }

  private scrollToLpRow(attribute: DlmsAttributes): void {
    document.getElementById(`lp-row-${attribute}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private get activeLpConfiguration(): SetupConfiguration[] {
    return this.lpConfiguration(this.activeLpTab);
  }

  private lpConfiguration(tab: string): SetupConfiguration[] {
    switch (tab) {
      case 'lp-2':
        return this.LP2SetupConfiguration;
      case 'lp-3':
        return this.LP3SetupConfiguration;
      default:
        return this.LP1SetupConfiguration;
    }
  }

  private isLpTabEnabled(tab: string): boolean {
    switch (tab) {
      case 'lp-2':
        return this.isAssociationSelected(DlmsAttributes.PushSetup_Interval2_ObjectList) ||
          this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod2_CaptureObjects);
      case 'lp-3':
        return this.isAssociationSelected(DlmsAttributes.PushSetup_Interval3_ObjectList) ||
          this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod3_CaptureObjects);
      default:
        return this.isAssociationSelected(DlmsAttributes.PushSetup_Interval1_ObjectList) ||
          this.isAssociationSelected(DlmsAttributes.ProfileGeneric_LoadProfilePeriod1_CaptureObjects);
    }
  }

  // preselects each recommended register once, as soon as its association and its load profile are both available
  private applyRecommendedDefaults(): void {
    if (!this.isFirstVersion) {
      return;
    }

    Object.keys(this.recommendedRegistersPerLpTab).forEach(tab => {
      if (!this.isLpTabEnabled(tab)) {
        delete this.lpDefaultsApplied[tab];
        return;
      }

      const applied = this.lpDefaultsApplied[tab] ??= new Set<DlmsAttributes>();
      const config = this.lpConfiguration(tab);

      this.recommendedRegistersPerLpTab[tab]
        .flatMap(group => group.registers ?? [])
        .forEach(register => {
          if (applied.has(register.attribute)) {
            return;
          }

          const configuration = config.find(c => c.attribute === register.attribute);

          if (!configuration || !configuration.shouldBeDisplayed || configuration.alwaysIncluded ||
            configuration.disabledByOtherList || configuration.configure ||
            this.isLpSelectionLimitReached(config)) {
            return;
          }

          applied.add(register.attribute);
          configuration.configure = true;
        });
    });

    this.updateLpDisabledFlags();
  }

  private buildRecommendedRow(register: RecommendedRegisterDto, config: SetupConfiguration[]): RecommendedRegisterRow {
    const configuration = config.find(c => c.attribute === register.attribute);

    if (!configuration || !(configuration.alwaysIncluded || configuration.shouldBeDisplayed)) {
      return toRecommendedRegisterRow(register, 'unavailable');
    }

    if (configuration.configure || configuration.alwaysIncluded) {
      return toRecommendedRegisterRow(register, 'selected', { locked: configuration.alwaysIncluded });
    }

    if (configuration.disabledByOtherList) {
      return toRecommendedRegisterRow(register, 'inOtherList', {
        otherLpTitleKey: this.findLpTabWithAttribute(register.attribute),
      });
    }

    return toRecommendedRegisterRow(register, 'missing');
  }

  private findLpTabWithAttribute(attribute: DlmsAttributes): string | undefined {
    const lists: [string, SetupConfiguration[]][] = [
      ['LOAD_PROFILE_1', this.LP1SetupConfiguration],
      ['LOAD_PROFILE_2', this.LP2SetupConfiguration],
      ['LOAD_PROFILE_3', this.LP3SetupConfiguration],
    ];

    return lists.find(([, config]) =>
      config !== this.activeLpConfiguration &&
      config.some(c => c.attribute === attribute && c.configure && !c.alwaysIncluded)
    )?.[0];
  }
}
